import { FlyerType } from '@/config/constants';
import {
  BodyLargeEmphasized,
  ButtonFlatMedium,
  ButtonPrimaryMedium,
} from '@/design-system';
import { css } from '@emotion/css';
import { Divider } from '../Divider';
import type { OptionType } from './FilterOption';
import { FilterOption } from './FilterOption';
import { Drawer } from '@/components';
import { ListItem } from '@mui/material';
import colors from '@/theme/colors';

type Props = {
   isOpen: boolean;
   title: string;
   viewResultsLabel: string;
   onClearFilters: () => void;
   onShowResults: () => void;
   onClose: (close: boolean) => void;
   handleToggle: (filterName: string, value: string, description: string) => () => void;
   disableClearAllButton: boolean;
   flyerType: string;
   filterSections: {
    grades_age: OptionType[],
    types:OptionType[],
   };
   localFilters: {
    grades_age: string[],
    types: string[],
   }
}

export const DrawerFilter: React.FC<Props> = (props) => {
  const { isOpen, title, viewResultsLabel, onClearFilters, onShowResults, disableClearAllButton, onClose, filterSections, flyerType, localFilters, handleToggle } = props;

  const renderList = () => {
    const filterKeys = Object.keys(filterSections);
    const filtersLength = filterKeys.length;

    return filterKeys.map((key, index) => {
      const isGradesAge = key === 'grades_age';
      const gradesAgeTitle = flyerType === FlyerType.school ? 'Grades' : 'Age';
      const title = isGradesAge ? gradesAgeTitle : key;
      const showDivider = index < filtersLength - 1;
      if (!filterSections[key] || filterSections[key].length === 0) return;

      const ariaLabel = `filter-${key}-label`;

      return (
          <div className={styles.content} key={index}  aria-labelledby={ariaLabel}>
            <BodyLargeEmphasized className={styles.filterName} id={ariaLabel}>
              {title}
            </BodyLargeEmphasized>
            <div className={styles.optionsContainer}>
                {filterSections[key].map(option => (
                  <FilterOption
                    {...option}
                    filterName={key}
                    key={option.value}
                    checked={localFilters[key].indexOf(option.value) !== -1}
                    handleToggle={handleToggle}
                  />
                ))}
            </div>
            {showDivider && (
              <div className={styles.divider}>
                <Divider />
              </div>
            )}
          </div>
      );
    });
  };

  return (
    <Drawer title={title} isOpen={isOpen} onToggle={onClose} full>
    <div className={styles.container}>
      {renderList()}
      <ListItem className={styles.footer}>
        <ButtonFlatMedium
          data-testid="clearAllButton"
          onClick={onClearFilters}
          disabled={disableClearAllButton}
        >
          Reset Filters
        </ButtonFlatMedium>
        <ButtonPrimaryMedium
          onClick={onShowResults}
          data-testid="showResultsButton"
        >
          {viewResultsLabel}
        </ButtonPrimaryMedium>
      </ListItem>
    </div>
  </Drawer>
  )
}


const styles = {
  container: css`
    display: flex;
    flex-flow: column nowrap;
    min-height: 90vh;
  `,
  footer: css`
    box-shadow: 0px -2px 8px rgba(0, 0, 0, 0.25);
    display: flex;
    position: sticky !important;
    background-color: ${colors.white};
    z-index: 1199 ;
    bottom: 0px;
    padding: 24px !important;
    margin-top: 25px;
    display: flex;
    justify-content: space-between !important;
    width: 100%;
  `,
  content: css`
    padding: 0px 16px;
    flex-grow: 1
  `,
  filterName: css`
    text-transform: capitalize;
  `,
  optionsContainer: css`
    padding-top: 7px;
  `,
  divider: css`
    padding: 15px 0px 24px 0px;
  `,
}
